import React, { createContext, useContext, useState, useEffect } from 'react';
import { networkManager } from '../services/NetworkManager';
import { LoginRequest, LoginResponse, User, AuthProviderProps, STORAGE_KEYS } from '../types';
import { useNotification } from './NotificationContext';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  loading: boolean;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LoginResponse['user'] | null>(null);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkAuth = () => {
      if (networkManager.isAuthenticated()) {
        // For now, we'll just set a basic user object
        // In a real app, you'd validate the token with the backend
        // Decode JWT to get user information
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const payload = JSON.parse(jsonPayload);
            
            setUser({
              id: payload._id || '1',
              email: payload.email || 'user@example.com',
              name: payload.name || 'User Profile',
              organizationID: payload.organizationID || ''
            });
          } catch (error) {
            setUser({
              id: '1',
              email: 'user@example.com',
              name: 'User Profile',
              organizationID: ''
            });
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);



  // Force re-render when user state changes
  useEffect(() => {
    if (user) {
      // Trigger a re-render by updating loading state
      const timer = setTimeout(() => {
        setLoading(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const login = async (credentials: LoginRequest) => {
    try {
      setLoading(true);
      const response = await networkManager.login(credentials);
      setUser(response.user);
    } catch (error: any) {
      setLoading(false);
      const errorMessage = error.message || 'Login failed. Please try again.';
      showNotification(errorMessage, 'error', 8000);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    networkManager.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
